import React, { useState } from 'react';
import { Button } from './ui/button';
import { Card, CardContent } from './ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { 
  FileText, 
  Download, 
  Trash2, 
  Eye, 
  File,
  FileImage,
  FileSpreadsheet,
  FileArchive,
  FileCode
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const EnhancedFileList = ({ files, onDelete, downloadEndpoint, showPreview = true }) => {
  const [previewFile, setPreviewFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);

  const formatFileSize = (bytes) => {
    if (!bytes) return 'Unknown size';
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  const getFileIcon = (filename) => {
    const ext = filename.split('.').pop().toLowerCase();
    
    const iconProps = { className: "w-8 h-8" };
    
    if (['pdf'].includes(ext)) {
      return <FileText {...iconProps} className="w-8 h-8 text-red-500" />;
    }
    if (['doc', 'docx'].includes(ext)) {
      return <FileText {...iconProps} className="w-8 h-8 text-blue-500" />;
    }
    if (['jpg', 'jpeg', 'png', 'gif', 'webp'].includes(ext)) {
      return <FileImage {...iconProps} className="w-8 h-8 text-green-500" />;
    }
    if (['xls', 'xlsx', 'csv'].includes(ext)) {
      return <FileSpreadsheet {...iconProps} className="w-8 h-8 text-emerald-500" />;
    }
    if (['zip', 'rar', '7z', 'tar', 'gz'].includes(ext)) {
      return <FileArchive {...iconProps} className="w-8 h-8 text-orange-500" />;
    }
    if (['js', 'jsx', 'ts', 'tsx', 'py', 'java', 'html', 'css'].includes(ext)) {
      return <FileCode {...iconProps} className="w-8 h-8 text-purple-500" />;
    }
    
    return <File {...iconProps} className="w-8 h-8 text-gray-500" />;
  };

  const getFileType = (filename) => {
    const ext = filename.split('.').pop().toLowerCase();
    return ext.toUpperCase();
  };

  const canPreview = (filename) => {
    const ext = filename.split('.').pop().toLowerCase();
    return ['pdf', 'jpg', 'jpeg', 'png', 'gif', 'webp'].includes(ext);
  };

  const handlePreview = async (file) => {
    if (!canPreview(file.filename)) {
      toast.info('Preview not available for this file type');
      return;
    }

    const token = localStorage.getItem('token');
    
    try {
      const response = await axios.get(
        `${API}${downloadEndpoint}/${file.stored_filename || file.filename}`,
        {
          headers: { Authorization: `Bearer ${token}` },
          responseType: 'blob'
        }
      );

      // Determine the correct MIME type
      const ext = file.filename.split('.').pop().toLowerCase();
      let mimeType = response.data.type;
      
      // Override if needed
      if (ext === 'pdf' && !mimeType.includes('pdf')) {
        mimeType = 'application/pdf';
      } else if (['jpg', 'jpeg'].includes(ext)) {
        mimeType = 'image/jpeg';
      } else if (ext === 'png') {
        mimeType = 'image/png';
      }

      const blob = new Blob([response.data], { type: mimeType });
      const url = window.URL.createObjectURL(blob);
      setPreviewUrl(url);
      setPreviewFile(file);
    } catch (error) {
      toast.error('Failed to load preview');
      console.error('Preview error:', error);
    }
  };

  const handleDownload = async (file) => {
    const token = localStorage.getItem('token');
    
    try {
      const response = await axios.get(
        `${API}${downloadEndpoint}/${file.stored_filename || file.filename}`,
        {
          headers: { Authorization: `Bearer ${token}` },
          responseType: 'blob'
        }
      );

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', file.filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success('File downloaded successfully');
    } catch (error) {
      toast.error('Failed to download file');
      console.error(error);
    }
  };

  const handleDelete = async (file) => {
    if (!window.confirm(`Delete "${file.filename}"?`)) return;
    
    const token = localStorage.getItem('token');
    
    try {
      await axios.delete(
        `${API}${downloadEndpoint}/${file.stored_filename || file.filename}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success('File deleted successfully');
      if (onDelete) onDelete(file.filename);
    } catch (error) {
      toast.error('Failed to delete file');
      console.error(error);
    }
  };

  const closePreview = () => {
    if (previewUrl) {
      window.URL.revokeObjectURL(previewUrl);
    }
    setPreviewUrl(null);
    setPreviewFile(null);
  };

  if (!files || files.length === 0) {
    return (
      <div className="text-center py-12 text-gray-500">
        <FileText className="w-16 h-16 mx-auto text-gray-300 mb-3" />
        <p className="text-lg font-medium">No files uploaded yet</p>
        <p className="text-sm text-gray-400 mt-1">Upload files to see them here</p>
      </div>
    );
  }

  return (
    <>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {files.map((file, index) => (
          <Card key={index} className="hover:shadow-md transition-shadow">
            <CardContent className="p-4">
              <div className="flex items-start space-x-4">
                {/* File Icon */}
                <div className="flex-shrink-0">
                  {getFileIcon(file.filename)}
                </div>

                {/* File Info */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-start justify-between">
                    <div className="flex-1 min-w-0">
                      <h4 className="text-sm font-semibold text-gray-900 truncate mb-1">
                        {file.filename}
                      </h4>
                      
                      <div className="flex flex-wrap items-center gap-2 text-xs text-gray-500">
                        <span className="px-2 py-0.5 bg-gray-100 rounded-full font-medium">
                          {getFileType(file.filename)}
                        </span>
                        
                        {file.size && (
                          <span className="flex items-center">
                            {formatFileSize(file.size)}
                          </span>
                        )}
                        
                        {file.uploaded_at && (
                          <>
                            <span>•</span>
                            <span>
                              {new Date(file.uploaded_at).toLocaleDateString('en-US', {
                                month: 'short',
                                day: 'numeric',
                                year: 'numeric'
                              })}
                            </span>
                          </>
                        )}
                      </div>

                      {file.uploaded_by && (
                        <p className="text-xs text-gray-400 mt-1">
                          Uploaded by {file.uploaded_by}
                        </p>
                      )}
                    </div>
                  </div>

                  {/* Action Buttons */}
                  <div className="flex items-center space-x-2 mt-3">
                    {showPreview && canPreview(file.filename) && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handlePreview(file)}
                        className="text-xs"
                      >
                        <Eye className="w-3 h-3 mr-1" />
                        Preview
                      </Button>
                    )}
                    
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleDownload(file)}
                      className="text-xs"
                    >
                      <Download className="w-3 h-3 mr-1" />
                      Download
                    </Button>
                    
                    {onDelete && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleDelete(file)}
                        className="text-xs text-red-600 hover:text-red-700 hover:bg-red-50"
                      >
                        <Trash2 className="w-3 h-3" />
                      </Button>
                    )}
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Preview Dialog */}
      <Dialog open={!!previewFile} onOpenChange={closePreview}>
        <DialogContent className="max-w-4xl max-h-[90vh]">
          <DialogHeader>
            <DialogTitle>{previewFile?.filename}</DialogTitle>
          </DialogHeader>
          
          <div className="overflow-auto max-h-[calc(90vh-120px)]">
            {previewUrl && previewFile && (
              <>
                {previewFile.filename.toLowerCase().endsWith('.pdf') ? (
                  <iframe
                    src={previewUrl}
                    className="w-full h-[600px] border-0"
                    title="PDF Preview"
                  />
                ) : (
                  <img
                    src={previewUrl}
                    alt={previewFile.filename}
                    className="max-w-full h-auto mx-auto"
                  />
                )}
              </>
            )}
          </div>

          <div className="flex justify-end space-x-2 mt-4">
            <Button variant="outline" onClick={closePreview}>
              Close
            </Button>
            <Button 
              onClick={() => handleDownload(previewFile)}
              style={{ backgroundColor: '#62D84E' }}
            >
              <Download className="w-4 h-4 mr-2" />
              Download
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default EnhancedFileList;
